import { useCallback, useEffect, useMemo, useState } from 'react';

type PaginationOptions<T> = {
  items: T[];
  itemsPerPage: number;
};

export type Pagination = {
  pageCount: number;
  currentPage: number;
  next: () => void;
  hasNext: boolean;
  previous: () => void;
  hasPrevious: boolean;
};

const usePagination = <T>({ items, itemsPerPage }: PaginationOptions<T>) => {
  const pageCount = Math.ceil(items.length / itemsPerPage);
  const pages = useMemo(
    () =>
      Array.from({ length: pageCount }, (_, i) =>
        items.slice(i * itemsPerPage, (i + 1) * itemsPerPage),
      ),
    [items, itemsPerPage, pageCount],
  );
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [items, itemsPerPage]);

  const next = useCallback(() => {
    setCurrentPage((prev) => (prev === pageCount ? 1 : prev + 1));
  }, [pageCount]);

  const previous = useCallback(() => {
    setCurrentPage((prev) => (prev === 1 ? pageCount : prev - 1));
  }, [pageCount]);

  return [
    pages[currentPage - 1] ?? [],
    {
      pageCount,
      currentPage,
      next,
      hasNext: currentPage < pageCount,
      previous,
      hasPrevious: currentPage > 1,
    },
  ] satisfies [T[], Pagination];
};

export default usePagination;
